import React, { useState, useEffect, useRef } from 'react';
import { GetTeams } from '../../../utilities/BL/SportPlayer';

/**
 * COMPONENTE VISUAL: SCORE OVERLAY (HIJO)
 * ------------------------------------------------------------------
 * Renderiza el marcador premium adaptado para transmisiones en vivo (OBS).
 * Maneja animaciones lineales de gol, notificaciones y patrocinio en tiempo real.
 * ------------------------------------------------------------------
 */
const Score = ({ data }) => {
    // --- ESTADO INTERNO DEL MARCADOR (PERSISTENTE) ---
    const [scoreboard, setScoreboard] = useState({
        homeScore: 0,
        awayScore: 0,
        timerSeconds: 0,
        isTimerRunning: false,
        addedTime: 0,
        periodName: "1er Tiempo",
        homeTeamName: "Local",
        awayTeamName: "Visita",
        scale: 1.0,
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        showSponsor: true,
        isVisible: false
    });

    // --- ESTADOS LOCALES DE ANIMACIÓN Y PROMOS ---
    const [isCelebrating, setIsCelebrating] = useState(false);
    const [isPromoActive, setIsPromoActive] = useState(false);
    const [activeNotification, setActiveNotification] = useState(null);

    const [teams, setTeams] = useState([]);

    useEffect(() => {
        const fetchTeamsList = async () => {
            try {
                const res = await GetTeams();
                if (res.success) setTeams(res.data);
            } catch (err) {
                console.error("Error loading teams list for scoreboard", err);
            }
        };
        fetchTeamsList();
    }, []);

    const getTeamLogo = (teamName) => {
        if (!teamName) return '';
        const foundTeam = teams.find(t => t.name.toLowerCase().trim() === teamName.toLowerCase().trim());
        if (foundTeam && foundTeam.logoUrl) {
            if (foundTeam.logoUrl.startsWith('http') || foundTeam.logoUrl.startsWith('data:')) {
                return foundTeam.logoUrl;
            }
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:50586/api/';
            const apiBase = apiUrl.replace('/api/', '');
            if (foundTeam.logoUrl.startsWith('/')) {
                return `${apiBase}${foundTeam.logoUrl}`;
            }
            return `${apiBase}/${foundTeam.logoUrl}`;
        }
        return `/img/games/lineup/teams/${teamName.toLowerCase().replace(/\s+/g, '-')}.png`;
    };
    
    // Referencia estable para el intervalo del cronómetro
    const intervalRef = useRef(null);

    // -------------------------------------------------------------------------
    // 1. ESCUCHA DE ACTUALIZACIONES (SOCKET)
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (!data) return;

        const { type, data: payload } = data;

        if (type === 'score.state_update') {
            setScoreboard(prev => {
                // PROTECCIÓN: Si el Admin manda una pausa sin sincronizar segundos,
                // evitamos que el overlay pise su tiempo acumulado local con un cero de base de datos.
                const isPauseTransition = !payload.isTimerRunning && prev.isTimerRunning;
                if (isPauseTransition && payload.timerSeconds === 0 && prev.timerSeconds > 0) {
                    return {
                        ...prev,
                        ...payload,
                        timerSeconds: prev.timerSeconds // Mantenemos los segundos que ya contamos en pantalla
                    };
                }
                return { ...prev, ...payload };
            });
        } else if (type === 'score.notification') {
            if (payload && payload.type === 'visibility_show') {
                setScoreboard(prev => ({ ...prev, isVisible: true }));
            } else if (payload && payload.type === 'visibility_hide') {
                setScoreboard(prev => ({ ...prev, isVisible: false }));
            } else {
                handleIncomingNotification(payload);
            }
        }
    }, [data]);

    // -------------------------------------------------------------------------
    // 2. MOTOR DEL CRONÓMETRO LOCAL (SEGUNDO A SEGUNDO)
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        if (scoreboard.isTimerRunning) {
            intervalRef.current = setInterval(() => {
                setScoreboard(prev => ({
                    ...prev,
                    timerSeconds: prev.timerSeconds + 1
                }));
            }, 1000);
        }

        return () => clearInterval(intervalRef.current);
    }, [scoreboard.isTimerRunning]);

    // -------------------------------------------------------------------------
    // 3. GESTOR DE ANIMACIONES EFÍMERAS Y PATROCINIOS LIVE
    // -------------------------------------------------------------------------
    const handleIncomingNotification = (notificationPayload) => {
        const durationMs = (notificationPayload.durationSeconds || 8) * 1000;

        // CASO A: Es la promo del patrocinador -> Se activa en el extremo derecho
        if (notificationPayload.type === 'promo') {
            setIsPromoActive(true);
            setTimeout(() => {
                setIsPromoActive(false);
            }, durationMs);
            return; // Evitamos que se pinte en la franja de abajo
        }

        // CASO B: Es un gol -> Activamos sacudida visual del marcador
        if (notificationPayload.type === 'goal') {
            setIsCelebrating(true);
            setTimeout(() => setIsCelebrating(false), 3000);
        }

        // Notificaciones estándar (Tarjetas, sustituciones) van a la franja inferior
        setActiveNotification(notificationPayload);
        setTimeout(() => {
            setActiveNotification(null);
        }, durationMs);
    };

    // --- HELPER DE FORMATEO ---
    const formatTime = (totalSeconds) => {
        const mins = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const secs = (totalSeconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };

    return (
        <div className="board-wrapper" style={{ transform: `scale(${scoreboard.scale})`, transformOrigin: 'top center' }}>
            
            {/* MARCADOR PRINCIPAL */}
            {scoreboard.isVisible && (
                <div 
                    className={`scoreboard-container ${isCelebrating ? 'celebrating' : ''}`} 
                    style={{ background: scoreboard.backgroundColor }}
                >
                    {/* Banner de Gol de fondo */}
                    <div className="goal-banner">
                        <span>¡GOOOOOL!</span>
                    </div>

                    {/* Patrocinio Fijo Izquierdo */}
                    {scoreboard.showSponsor && (
                        <div className="sponsor-box">
                            <img 
                                src={scoreboard.sponsorLogo || "/img/overlays/score/sponsor.png"} 
                                alt="Sponsor" 
                                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerText = 'PATROCINIO'; }}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                        </div>
                    )}

                    {/* Equipo Local */}
                    <div className="team-box">
                        <div className="team-logo">
                            <img 
                                src={getTeamLogo(scoreboard.homeTeamName)} 
                                alt="L" 
                                onError={(e) => { 
                                    const backupUrl = `/img/overlays/score/teams/${scoreboard.homeTeamName.toLowerCase()}.png`;
                                    if (e.target.src !== window.location.origin + backupUrl) {
                                        e.target.src = backupUrl;
                                    } else {
                                        e.target.style.opacity = '0'; 
                                    }
                                }} 
                            />
                        </div>
                        <div className="team-name" style={{ color: scoreboard.textColor }}>{scoreboard.homeTeamName}</div>
                    </div>

                    {/* Marcador Numérico */}
                    <div className="score-box" style={{ color: scoreboard.numberColor }}>
                        <span>{scoreboard.homeScore}</span>
                        <span className="divider">:</span>
                        <span>{scoreboard.awayScore}</span>
                    </div>

                    {/* Equipo Visitante */}
                    <div className="team-box">
                        <div className="team-name text-right" style={{ color: scoreboard.textColor }}>{scoreboard.awayTeamName}</div>
                        <div className="team-logo team-logo--right">
                            <img 
                                src={getTeamLogo(scoreboard.awayTeamName)} 
                                alt="V" 
                                onError={(e) => { 
                                    const backupUrl = `/img/overlays/score/teams/${scoreboard.awayTeamName.toLowerCase()}.png`;
                                    if (e.target.src !== window.location.origin + backupUrl) {
                                        e.target.src = backupUrl;
                                    } else {
                                        e.target.style.opacity = '0'; 
                                    }
                                 }} 
                            />
                        </div>
                    </div>

                    {/* Cronómetro y Periodo */}
                    <div className="timer-box" style={{ backgroundColor: scoreboard.timerBgColor }}>
                        <div className="time-wrapper">
                            <div className="time">{formatTime(scoreboard.timerSeconds)}</div>
                            {scoreboard.addedTime > 0 && (
                                <div className="added-time">+{scoreboard.addedTime}</div>
                            )}
                        </div>
                        <div className="period" style={{ color: scoreboard.textColor }}>{scoreboard.periodName}</div>
                    </div>

                    {/* PROMO LIVE DEL PATROCINADOR (EXTREMO DERECHO INTERNO) */}
                    <div className={`live-sponsor-box ${isPromoActive ? 'active' : ''}`}>
                        <img 
                            src={scoreboard.promoLogo || "/img/overlays/score/sponsorLive.png"} 
                            alt="Promo" 
                            onError={(e) => e.target.style.opacity = '0.5'} 
                        /> 
                    </div>
                </div>
            )}

            {/* BANNER DE NOTIFICACIONES INFERIOR */}
            <div className={`notification-banner ${activeNotification ? 'active' : ''}`}>
                {activeNotification && (
                    <div 
                        className="notification-content" 
                        dangerouslySetInnerHTML={{ __html: activeNotification.htmlContent }} 
                    />
                )}
            </div>

        </div>
    );
};

export { Score };









// import React, { useState, useEffect, useRef } from 'react';

// /**
//  * COMPONENTE VISUAL: SCORE OVERLAY (HIJO)
//  * ------------------------------------------------------------------
//  * Renderiza el marcador premium adaptado para transmisiones en vivo (OBS).
//  * Maneja animaciones lineales de gol y notificaciones dinámicas.
//  */
// const Score = ({ data }) => {
//     // --- ESTADO INTERNO DEL MARCADOR ---
//     const [scoreboard, setScoreboard] = useState({
//         homeScore: 0, awayScore: 0, timerSeconds: 0,
//         isTimerRunning: false, addedTime: 0,
//         periodName: "1er Tiempo", homeTeamName: "Local",
//         awayTeamName: "Visita", scale: 1.0,
//         backgroundColor: "rgba(15, 23, 42, 0.95)", showSponsor: true
//     });

//     const [localSeconds, setLocalSeconds] = useState(0);
//     const [isCelebrating, setIsCelebrating] = useState(false);
//     const [activeNotification, setActiveNotification] = useState(null);
    
//     // Usamos un ref para el intervalo y así evitar conflictos de renderizado
//     const timerIntervalRef = useRef(null);

//     // -------------------------------------------------------------------------
//     // 1. ESCUCHA DE ACTUALIZACIONES (SOCKET)
//     // -------------------------------------------------------------------------
//     useEffect(() => {
//         if (!data) return;

//         const { type, data: payload } = data;

//         if (type === 'score.state_update') {
//             setScoreboard(prev => ({ ...prev, ...payload }));
//         } else if (type === 'score.notification') {
//             handleIncomingNotification(payload);
//         }
//     }, [data]);

//     // -------------------------------------------------------------------------
//     // 2. MOTOR DEL CRONÓMETRO (CORREGIDO)
//     // -------------------------------------------------------------------------
//     // Sincronización única cuando el servidor cambia el valor absoluto
//     // useEffect(() => {
//     //     setLocalSeconds(scoreboard.timerSeconds);
//     // }, [scoreboard.timerSeconds]);

//     // // Gestión del intervalo
//     // useEffect(() => {
//     //     if (scoreboard.isTimerRunning) {
//     //         // Solo iniciamos si no hay uno ya corriendo
//     //         if (!timerIntervalRef.current) {
//     //             timerIntervalRef.current = setInterval(() => {
//     //                 setLocalSeconds(prev => prev + 1);
//     //             }, 1000);
//     //         }
//     //     } else {
//     //         // Pausar
//     //         if (timerIntervalRef.current) {
//     //             clearInterval(timerIntervalRef.current);
//     //             timerIntervalRef.current = null;
//     //         }
//     //     }

//     //     return () => {
//     //         if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
//     //     };
//     // }, [scoreboard.isTimerRunning]);

//     // Sincronización absoluta: cada vez que el scoreboard cambia, 
//     // forzamos el valor del cronómetro a lo que diga el servidor.
//     useEffect(() => {
//         console.log("Servidor dice que el tiempo es:", scoreboard.timerSeconds);
//         setLocalSeconds(scoreboard.timerSeconds);
//     }, [scoreboard.timerSeconds]);

//     useEffect(() => {
//         let timerInterval = null;

//         if (scoreboard.isTimerRunning) {
//             // Iniciamos solo si está corriendo
//             timerInterval = setInterval(() => {
//                 setLocalSeconds(prev => prev + 1);
//             }, 1000);
//         } else {
//             // Al pausar o resetear (isTimerRunning: false), limpiamos
//             clearInterval(timerInterval);
//         }

//         return () => clearInterval(timerInterval);
//     }, [scoreboard.isTimerRunning]);

//     // -------------------------------------------------------------------------
//     // 3. GESTOR DE NOTIFICACIONES
//     // -------------------------------------------------------------------------
//     const handleIncomingNotification = (notificationPayload) => {
//         const durationMs = (notificationPayload.durationSeconds || 8) * 1000;

//         // Si es Gol, disparamos la animación grande
//         if (notificationPayload.type === 'goal') {
//             setIsCelebrating(true);
//             setTimeout(() => setIsCelebrating(false), 3000);
//         }

//         // Mostramos el banner inferior
//         setActiveNotification(notificationPayload);
//         setTimeout(() => setActiveNotification(null), durationMs);
//     };

//     // --- HELPER FORMATO ---
//     const formatTime = (totalSeconds) => {
//         const mins = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
//         const secs = (totalSeconds % 60).toString().padStart(2, '0');
//         return `${mins}:${secs}`;
//     };

//     // --- ESTILOS DINÁMICOS ---
//     const wrapperStyles = {
//         transform: `scale(${scoreboard.scale})`,
//         transformOrigin: 'top center'
//     };

//     const containerStyles = { background: scoreboard.backgroundColor };

//     return (
//         <div className="board-wrapper" style={wrapperStyles} key={scoreboard.timerSeconds}>
//             <div className={`scoreboard-container ${isCelebrating ? 'celebrating' : ''}`} style={containerStyles}>
                
//                 <div className="goal-banner">
//                     <span>¡GOOOOOL!</span>
//                 </div>

//                 {scoreboard.showSponsor && (
//                     <div className="sponsor-box">
//                         <img src="/img/overlays/score/sponsor.png" alt="Sponsor" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerText = 'PATROCINIO'; }} />
//                     </div>
//                 )}

//                 <div className="team-box">
//                     <div className="team-logo">
//                         <img src={`/img/overlays/score/teams/${scoreboard.homeTeamName.toLowerCase()}.png`} alt="L" onError={(e) => e.target.style.opacity = '0'} />
//                     </div>
//                     <div className="team-name">{scoreboard.homeTeamName}</div>
//                 </div>

//                 <div className="score-box">
//                     <span>{scoreboard.homeScore}</span>
//                     <span className="divider">:</span>
//                     <span>{scoreboard.awayScore}</span>
//                 </div>

//                 <div className="team-box">
//                     <div className="team-name text-right">{scoreboard.awayTeamName}</div>
//                     <div className="team-logo team-logo--right">
//                         <img src={`/img/overlays/score/teams/${scoreboard.awayTeamName.toLowerCase()}.png`} alt="V" onError={(e) => e.target.style.opacity = '0'} />
//                     </div>
//                 </div>

//                 <div className="timer-box">
//                     <div className="time-wrapper">
//                         <div className="time">{formatTime(localSeconds)}</div>
//                         {scoreboard.addedTime > 0 && <div className="added-time">+{scoreboard.addedTime}</div>}
//                     </div>
//                     <div className="period">{scoreboard.periodName}</div>
//                 </div>
//             </div>

//             <div className={`notification-banner ${activeNotification ? 'active' : ''}`}>
//                 {activeNotification && (
//                     <div className="notification-content" dangerouslySetInnerHTML={{ __html: activeNotification.htmlContent }} />
//                 )}
//             </div>
//         </div>
//     );
// };

// export { Score };









// import React, { useState, useEffect } from 'react';

// /**
//  * COMPONENTE VISUAL: SCORE OVERLAY (HIJO)
//  * ------------------------------------------------------------------
//  * Renderiza el marcador premium adaptado para transmisiones en vivo (OBS).
//  * Maneja animaciones lineales de gol y notificaciones dinámicas.
//  * ------------------------------------------------------------------
//  */
// const Score = ({ data }) => {
//     // --- ESTADO INTERNO DEL MARCADOR (PERSISTENTE) ---
//     const [scoreboard, setScoreboard] = useState({
//         homeScore: 0,
//         awayScore: 0,
//         timerSeconds: 0,
//         isTimerRunning: false,
//         addedTime: 0,
//         periodName: "1er Tiempo",
//         homeTeamName: "Local",
//         awayTeamName: "Visita",
//         scale: 1.0,
//         backgroundColor: "rgba(15, 23, 42, 0.95)",
//         showSponsor: true
//     });

//     // --- ESTADOS LOCALES DE APOYO ---
//     const [localSeconds, setLocalSeconds] = useState(0);
//     const [isCelebrating, setIsCelebrating] = useState(false);
//     const [activeNotification, setActiveNotification] = useState(null);

//     // -------------------------------------------------------------------------
//     // 1. ESCUCHA DE ACTUALIZACIONES (ESTADO VS EVENTOS)
//     // -------------------------------------------------------------------------
//     useEffect(() => {
//         if (!data) return;

//         // Desestructuramos el evento que viene desde el backend
//         const { type, data: payload } = data;

//         if (type === 'score.state_update') {
//             setScoreboard(payload);
//         } else if (type === 'score.notification') {
//             handleIncomingNotification(payload);
//         }
//     }, [data]);

//     // -------------------------------------------------------------------------
//     // 2. MOTOR DEL CRONÓMETRO LOCAL (SINCRONIZADO)
//     // -------------------------------------------------------------------------
//     useEffect(() => {
//         setLocalSeconds(scoreboard.timerSeconds);
//     }, [scoreboard.timerSeconds]);

//     useEffect(() => {
//         let timerInterval = null;

//         if (scoreboard.isTimerRunning) {
//             timerInterval = setInterval(() => {
//                 setLocalSeconds(prev => prev + 1);
//             }, 1000);
//         } else {
//             //if (timerInterval) clearInterval(timerInterval);
//             clearInterval(timerInterval);
//         }

//         return () => {
//             //if (timerInterval) clearInterval(timerInterval);
//             clearInterval(timerInterval);
//         };
//     }, [scoreboard.isTimerRunning]);

//     // Limpiar el intervalo cuando el admin mande reset
//     useEffect(() => {
//         if (!scoreboard.isTimerRunning && scoreboard.timerSeconds === 0) {
//             setLocalSeconds(0);
//         }
//     }, [scoreboard.timerSeconds, scoreboard.isTimerRunning]);

//     // En lugar de enviar un objeto gigante, envía solo lo que cambia
//     const toggleTimer = () => {
//         // Solo cambiamos el booleano, NO enviamos timerSeconds
//         handleUpdateScore({ isTimerRunning: !scoreboard.isTimerRunning });
//     };

//     const resetTimer = () => {
//         // Aquí sí enviamos el 0 explícito
//         handleUpdateScore({ timerSeconds: 0, isTimerRunning: false });
//     };

//     // -------------------------------------------------------------------------
//     // 3. GESTOR DE ANIMACIONES EFÍMERAS
//     // -------------------------------------------------------------------------
//     const handleIncomingNotification = (notificationPayload) => {
//         const { type: notifType, htmlContent, durationSeconds } = notificationPayload;
//         const durationMs = (durationSeconds || 8) * 1000;

//         // if (notifType === 'goal') {
//         //     // Activa animación de temblor y banner de GOL en el marcador
//         //     setIsCelebrating(true);
//         //     setTimeout(() => {
//         //         setIsCelebrating(false);
//         //     }, durationMs);
//         // } else {
//         //     // Despliega banner inferior (tarjetas, cambios, promos)
//         //     setActiveNotification(notificationPayload);
//         //     setTimeout(() => {
//         //         setActiveNotification(null);
//         //     }, durationMs);
//         // }

//         // Ahora TODOS los eventos (gol, tarjetas) usan la franja inferior.
//         // Si quieres que el banner de GOL sea especial, puedes ponerle una clase CSS diferente.
//         // Despliega banner inferior (tarjetas, cambios, promos)
//             setActiveNotification(notificationPayload);
//             setTimeout(() => {
//                 setActiveNotification(null);
//             }, (notificationPayload.durationSeconds || 8) * 1000);
//     };

//     // --- HELPER DE FORMATEO DE TIEMPO ---
//     const formatTime = (totalSeconds) => {
//         const mins = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
//         const secs = (totalSeconds % 60).toString().padStart(2, '0');
//         return `${mins}:${secs}`;
//     };

//     // --- MAPEO DE ESTILOS DINÁMICOS ---
//     const wrapperStyles = {
//         transform: `scale(${scoreboard.scale})`,
//         transformOrigin: 'top center'
//     };

//     const containerStyles = {
//         background: scoreboard.backgroundColor
//     };

//     return (
//         <div className="board-wrapper" style={wrapperStyles}>
            
//             {/* MARCADOR PRINCIPAL */}
//             <div 
//                 className={`scoreboard-container ${isCelebrating ? 'celebrating' : ''}`} 
//                 style={containerStyles}
//             >
//                 {/* Banner de Gol (Se activa por CSS mediante la clase celebrating) */}
//                 <div className="goal-banner">
//                     <span>¡GOOOOOL!</span>
//                 </div>

//                 {/* Caja de Patrocinio Fijo */}
//                 {scoreboard.showSponsor && (
//                     <div className="sponsor-box">
//                         <img 
//                             src="/img/overlays/score/sponsor.png" 
//                             alt="Sponsor" 
//                             onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerText = 'PATROCINIO'; }}
//                         />
//                     </div>
//                 )}

//                 {/* Equipo Local */}
//                 <div className="team-box">
//                     <div className="team-logo">
//                         <img src={`/img/overlays/score/teams/${scoreboard.homeTeamName.toLowerCase()}.png`} alt="L" onError={(e) => e.target.style.opacity = '0'} />
//                     </div>
//                     <div className="team-name">{scoreboard.homeTeamName}</div>
//                 </div>

//                 {/* Dígitos del Marcador */}
//                 <div className="score-box">
//                     <span>{scoreboard.homeScore}</span>
//                     <span className="divider">:</span>
//                     <span>{scoreboard.awayScore}</span>
//                 </div>

//                 {/* Equipo Visitante */}
//                 <div className="team-box">
//                     <div className="team-name text-right">{scoreboard.awayTeamName}</div>
//                     <div className="team-logo team-logo--right">
//                         <img src={`/img/overlays/score/teams/${scoreboard.awayTeamName.toLowerCase()}.png`} alt="V" onError={(e) => e.target.style.opacity = '0'} />
//                     </div>
//                 </div>

//                 {/* Cronómetro y Periodo */}
//                 <div className="timer-box">
//                     <div className="time-wrapper">
//                         <div className="time">{formatTime(localSeconds)}</div>
//                         {scoreboard.addedTime > 0 && (
//                             <div className="added-time">+{scoreboard.addedTime}</div>
//                         )}
//                     </div>
//                     <div className="period">{scoreboard.periodName}</div>
//                 </div>
//             </div>

//             {/* BANNER DE NOTIFICACIONES (INYECTADO DINÁMICAMENTE DESDE EL ADMIN) */}
//             <div className={`notification-banner ${activeNotification ? 'active' : ''}`}>
//                 {activeNotification && (
//                     <div 
//                         className="notification-content" 
//                         dangerouslySetInnerHTML={{ __html: activeNotification.htmlContent }} 
//                     />
//                 )}
//             </div>

//         </div>
//     );
// };

// export { Score };